import { cloneDeep, groupBy, isNil, merge, omit, orderBy } from 'lodash';
import * as yup from 'yup';
import {
  PermissionType,
  RolePermission,
  RolePermissionFormField,
  RolePermissionTemplate,
} from '../../api/graphql/generated';
import { i18nKeyType } from '../../i18n';
import { logger } from '../../util/logger';
import {
  MultiSelectRolePermissionUserdata,
  NumericRolePermissionUserdata,
  RolePermissionInputType,
  RolePermissionSelectMetadata,
  RolePermissionTemplateMetadata,
  RolePermissionUserdata,
} from './role.types';

export const EntityToLocalizedText: Partial<
  Record<PermissionType, i18nKeyType>
> = {
  [PermissionType.SCRATCHPAD_PLAYLIST]: 'common:playlist',
  [PermissionType.CHART]: 'common:chart',
  [PermissionType.MACHINE]: 'common:machine',
  [PermissionType.SCRATCHPAD]: 'common:part',
};

export type SortedPermissionFormFields = {
  [entity: string]: RolePermissionFormField[];
};

export const sortRolePermissionFormData = (params: {
  rolePermissionFormFields: RolePermissionFormField[];
}): SortedPermissionFormFields => {
  const { rolePermissionFormFields } = params;

  const sortedPermissionFormFields = orderBy(
    rolePermissionFormFields,
    [
      'template.entity',
      'template.appSlug',
      (f) => {
        const isEntityField = f.template.metadata.subject === f.template.entity;
        return isEntityField ? -1 : 0;
      },
      'template.displayOrder',
    ],
    ['asc', 'desc', 'asc', 'asc']
  );

  const formFieldsByEntity = groupBy(
    sortedPermissionFormFields,
    'template.entity'
  );

  return formFieldsByEntity;
};

export interface RolePermissionFormValue {
  can: boolean;
  rolePermissionInputType?: string;
  rolePermissionInputValue?: unknown;
}

export type RolePermissionForm = {
  [policy: string]: RolePermissionFormValue;
};

export type MultiSelectRolePermissionFormInputValue = Record<string, boolean>;

/**
 * Converts a list of role permission form fields to an object that represents
 * the form values.
 */
export const convertRolePermissionFormFieldsToForm = (params: {
  rolePermissionFormFields: RolePermissionFormField[];
}): RolePermissionForm => {
  const { rolePermissionFormFields } = params;

  const formFields: RolePermissionForm = {};

  for (const permissionFormField of rolePermissionFormFields) {
    const policy = permissionFormField.template.policy;
    const userData: RolePermissionUserdata | undefined =
      permissionFormField.permission?.userData;
    const metadata: RolePermissionTemplateMetadata =
      permissionFormField.template.metadata;
    const defaultRolePermissionInputType = metadata.rolePermissionInputType[0];

    let rolePermissionInputValue = userData?.rolePermissionInputValue;

    if ('multiple' in metadata && metadata.multiple) {
      rolePermissionInputValue = (
        userData as MultiSelectRolePermissionUserdata
      )?.rolePermissionInputValue.reduce((acc, value) => {
        acc[value] = true;
        return acc;
      }, {} as MultiSelectRolePermissionFormInputValue);
    }

    formFields[policy] = {
      can: permissionFormField.permission?.can ?? false,
      rolePermissionInputType:
        userData?.rolePermissionInputType ?? defaultRolePermissionInputType,
      rolePermissionInputValue,
    };
  }

  return formFields;
};

export const getYupSchemaForRolePermissionForm = (params: {
  rolePermissionFormFields: RolePermissionFormField[];
}): yup.AnySchema => {
  const { rolePermissionFormFields } = params;

  const formSchemaShape: Record<string, yup.AnySchema> = {};

  for (const permissionFormField of rolePermissionFormFields) {
    const policy = permissionFormField.template.policy;
    formSchemaShape[policy] = buildYupPermissionFormFieldSchema({
      permissionFormField,
    });
  }

  return yup.object().shape(formSchemaShape);
};

export const buildYupPermissionFormFieldSchema = (params: {
  permissionFormField: RolePermissionFormField;
}): yup.AnySchema => {
  const { permissionFormField } = params;

  const metadata = permissionFormField.template
    .metadata as RolePermissionTemplateMetadata;
  const userData: RolePermissionUserdata | undefined =
    permissionFormField.permission?.userData;
  const can = permissionFormField.permission?.can;

  const rolePermissionSchemaShape: yup.AnyObject = {
    can: yup.boolean().required(),
  };

  if (metadata.rolePermissionInputType.length < 1) {
    return yup.object().shape(rolePermissionSchemaShape);
  }

  // if the input types are 1 or more, then the input type is required
  rolePermissionSchemaShape.rolePermissionInputType = yup.string().required();

  if (metadata.rolePermissionInputType.length === 1) {
    return buildSingleInputTypeSchema({
      metadata,
      userData,
      can,
      rolePermissionSchemaShape,
    });
  }

  return buildMultipleInputTypeSchema({
    metadata,
    userData,
    can,
    rolePermissionSchemaShape,
  });
};

const buildSingleInputTypeSchema = (params: {
  metadata: RolePermissionTemplateMetadata;
  userData: RolePermissionUserdata | undefined;
  can: boolean | undefined;
  rolePermissionSchemaShape: yup.AnyObject;
}): yup.AnySchema => {
  const { metadata, userData, can, rolePermissionSchemaShape } = params;
  // present an input that is a single type
  const inputType = metadata.rolePermissionInputType[0];
  const isNumeric = isRolePermissionFormFieldNumeric({ metadata });

  if (isNumeric) {
    return yup.object().shape({
      ...rolePermissionSchemaShape,
      rolePermissionInputValue: getYupRolePermissionNumericSchema({
        metadata,
        userData,
        can: can ?? false,
      }),
    });
  }

  if (inputType === RolePermissionInputType.SELECT) {
    return buildSelectInputTypeSchema(
      metadata as RolePermissionSelectMetadata,
      rolePermissionSchemaShape
    );
  }

  return yup.object().shape(rolePermissionSchemaShape);
};

const buildSelectInputTypeSchema = (
  selectMetadata: RolePermissionSelectMetadata,
  rolePermissionSchemaShape: yup.AnyObject
): yup.AnySchema => {
  const options = selectMetadata.options.map((option) => String(option.value));
  const isMultiple = selectMetadata.multiple;

  if (isMultiple) {
    const optionsShape: Record<string, yup.AnySchema> = {};
    options.forEach((option) => {
      optionsShape[option] = yup.boolean();
    });

    return yup.object().shape({
      ...rolePermissionSchemaShape,
      rolePermissionInputValue: yup.object().shape(optionsShape),
    });
  }

  return yup.object().shape({
    ...rolePermissionSchemaShape,
    rolePermissionInputValue: yup.string().oneOf(options),
  });
};

/**
 * For a role permission with multiple input types (user selected), we build a
 * schema that matches the selected input type.
 */
const buildMultipleInputTypeSchema = (params: {
  metadata: RolePermissionTemplateMetadata;
  userData: RolePermissionUserdata | undefined;
  can: boolean | undefined;
  rolePermissionSchemaShape: yup.AnyObject;
}): yup.AnySchema => {
  const { metadata, userData, can, rolePermissionSchemaShape } = params;
  const isNumeric = isRolePermissionFormFieldNumeric({ metadata });

  // present an input that allows the user to select what kind of input
  if (isNumeric) {
    return yup.object().shape({
      ...rolePermissionSchemaShape,
      rolePermissionInputValue: getYupRolePermissionNumericSchema({
        metadata,
        userData,
        can: can ?? false,
      }),
    });
  }

  return yup.object().shape(rolePermissionSchemaShape);
};

export const isRolePermissionFormFieldNumeric = (params: {
  metadata: RolePermissionTemplateMetadata;
}): boolean => {
  const { metadata } = params;

  const numericTypes = [
    RolePermissionInputType.NUMBER,
    RolePermissionInputType.PERCENTAGE,
  ];

  return metadata.rolePermissionInputType.every((type) =>
    numericTypes.includes(type)
  );
};

/**
 * Uses the user input and metadata to determine the schema for a numeric field
 */
export const getYupRolePermissionNumericSchema = (params: {
  metadata: RolePermissionTemplateMetadata;
  userData?: RolePermissionUserdata;
  can: boolean;
}): yup.AnySchema => {
  const { metadata, userData, can } = params;

  let schema = yup.number();

  if (can) {
    schema = schema.required();
  }

  const isSetByUser = userData?.rolePermissionInputType !== undefined;
  const inputType = isSetByUser
    ? userData.rolePermissionInputType
    : metadata.rolePermissionInputType[0];

  if (inputType === RolePermissionInputType.PERCENTAGE) {
    schema = schema.min(0).max(100);
  }

  return schema;
};

export const applyFormToRolePermissionFormFields = (params: {
  form: RolePermissionForm;
  rolePermissionFormFields: RolePermissionFormField[];
}): RolePermissionFormField[] => {
  const { form, rolePermissionFormFields } = params;

  return rolePermissionFormFields.map((permissionFormField) => {
    const policy = permissionFormField.template.policy;
    const formValue: RolePermissionFormValue | undefined = form[policy];

    if (!formValue) {
      return permissionFormField;
    }

    const updatedPermissionFields: Partial<RolePermission> = {
      can: formValue.can,
      userData: {
        rolePermissionInputType: formValue.rolePermissionInputType,
        rolePermissionInputValue: convertFormValueToUserdataValue({
          formValue,
          template: permissionFormField.template,
        }),
      },
    };

    const updatedFormField = merge(cloneDeep(permissionFormField), {
      permission: updatedPermissionFields,
    });

    return updatedFormField;
  });
};

type RolePermissionToSave = Omit<
  RolePermission,
  'id' | '__typename' | 'isBaseRolePermission' | 'rolePermissionTemplate'
> & {
  id?: string;
};

export const buildRolePermissionsFromForm = (params: {
  roleId: string;
  form: RolePermissionForm;
  rolePermissionFormFields: RolePermissionFormField[];
}): RolePermissionToSave[] => {
  const { form, rolePermissionFormFields, roleId } = params;

  const permissions: RolePermissionToSave[] = [];

  for (const permissionFormField of rolePermissionFormFields) {
    const template = permissionFormField.template;
    const policy = permissionFormField.template.policy;
    const formValue = form[policy];

    if (!formValue) {
      logger.error('Failed to find form value for policy.', {
        policy,
        templateId: template.id,
      });
    }

    const defaultPermission: RolePermissionToSave = {
      roleId,
      rolePermissionTemplateId: template.id,
      appStoreAppId: template.appStoreAppId,
      appSlug: template.appSlug,
      entity: template.entity,
      policy,
      subject: template.metadata.subject,
      conditions: template.metadata.conditions,
      can: false,
      fieldName: template.metadata.fieldName,
      fieldApiName: template.metadata.fieldApiName,
    };

    const updatedPermissionValues: Partial<RolePermissionToSave> =
      formValue && {
        can: formValue.can,
        userData: {
          rolePermissionInputType: formValue.rolePermissionInputType,
          rolePermissionInputValue: convertFormValueToUserdataValue({
            formValue,
            template,
          }),
        },
      };

    const mergedPermission: RolePermissionToSave = merge<
      RolePermissionToSave,
      RolePermissionToSave,
      Partial<RolePermissionToSave>
    >(
      defaultPermission,
      // we omit the userData to always accept the incoming one
      omit(
        permissionFormField.permission,
        '__typename',
        'userData',
        'isBaseRolePermission',
        'rolePermissionTemplate'
      ),
      updatedPermissionValues
    );

    permissions.push(mergedPermission);
  }

  return permissions;
};

export const convertFormValueToUserdataValue = (params: {
  formValue: RolePermissionFormValue;
  template: RolePermissionTemplate;
}): unknown => {
  const { formValue, template } = params;

  const inputValue = formValue?.rolePermissionInputValue;

  if (
    formValue.rolePermissionInputType === RolePermissionInputType.NUMBER ||
    formValue.rolePermissionInputType === RolePermissionInputType.PERCENTAGE
  ) {
    return isNil(inputValue)
      ? undefined
      : Number(formValue.rolePermissionInputValue);
  } else if (
    formValue.rolePermissionInputType === RolePermissionInputType.SELECT
  ) {
    const selectMetadata = template.metadata as RolePermissionSelectMetadata;
    const selectInputValue = (inputValue ??
      {}) as MultiSelectRolePermissionFormInputValue;

    if (selectMetadata.multiple) {
      return Object.keys(selectInputValue).filter((key) => {
        return selectInputValue[key];
      });
    }

    return formValue.rolePermissionInputValue;
  }
};

export const isNumericRolePermissionUserdata = (
  userData: RolePermissionUserdata
): userData is NumericRolePermissionUserdata => {
  return (
    userData?.rolePermissionInputType === RolePermissionInputType.NUMBER ||
    userData?.rolePermissionInputType === RolePermissionInputType.PERCENTAGE
  );
};
